"use client";

import { Check, Sparkles, X } from "lucide-react";
import { useEffect, useRef } from "react";
import type { GhostSuggestion, SoapSection } from "@/src/services/mockAiService";
import { useLang } from "@/src/i18n";

type SoapCardProps = {
  id: SoapSection;
  label: string;
  hint: string;
  value: string;
  suggestion?: GhostSuggestion;
  isFocused: boolean;
  aiAuthored?: boolean;
  generating?: boolean;
  onFocus: (field: SoapSection) => void;
  onChange: (field: SoapSection, value: string) => void;
  onAcceptSuggestion: (suggestion: GhostSuggestion) => void;
  onDismissSuggestion: (field: SoapSection) => void;
  children?: React.ReactNode;
};

export function SoapCard({
  id,
  label,
  hint,
  value,
  suggestion,
  isFocused,
  aiAuthored,
  generating,
  onFocus,
  onChange,
  onAcceptSuggestion,
  onDismissSuggestion,
  children,
}: SoapCardProps) {
  const { t } = useLang();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaret = useRef<[number, number] | null>(null);
  const letter = id.charAt(0).toUpperCase(); // S / O / A / P — language-agnostic

  // Grow the box to fit its content — including text inserted programmatically
  // (scribe / accept / auto-pilot), which does not fire onInput. Also restore
  // the caret after a programmatic list edit (bullet continue / indent).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(82, el.scrollHeight)}px`;
    if (pendingCaret.current) {
      const [s, e] = pendingCaret.current;
      el.selectionStart = s;
      el.selectionEnd = e;
      pendingCaret.current = null;
    }
  }, [value]);

  // Apply a programmatic edit + remember where the caret should land.
  function editValue(next: string, caret: number, caretEnd = caret) {
    pendingCaret.current = [caret, caretEnd];
    onChange(id, next);
  }

  // Markdown-style list editing inside the plain-text SOAP box:
  //  • Enter continues a "- ", "* " or "1." list (auto-increments numbers;
  //    an empty bullet exits the list)
  //  • Tab indents the current/selected lines (sub-bullet), Shift+Tab outdents
  //  (Cmd+Tab is an OS app-switch shortcut and can't be intercepted, so
  //   Shift+Tab is the "go back a level" key.)
  function handleListKeys(event: React.KeyboardEvent<HTMLTextAreaElement>): boolean {
    const el = textareaRef.current;
    if (!el) return false;
    const v = el.value;
    const selStart = el.selectionStart;
    const selEnd = el.selectionEnd;

    if (event.key === "Enter" && !event.shiftKey && selStart === selEnd) {
      const lineStart = v.lastIndexOf("\n", selStart - 1) + 1;
      const line = v.slice(lineStart, selStart);
      const m = line.match(/^(\s*)([-*]|\d+\.)(\s+)(.*)$/);
      if (m) {
        const [, indent, marker, , content] = m;
        if (!content.trim()) {
          event.preventDefault();
          editValue(v.slice(0, lineStart) + v.slice(selEnd), lineStart);
          return true;
        }
        const nextMarker = /^\d+\.$/.test(marker) ? `${parseInt(marker, 10) + 1}.` : marker;
        const insert = `\n${indent}${nextMarker} `;
        event.preventDefault();
        editValue(v.slice(0, selStart) + insert + v.slice(selEnd), selStart + insert.length);
        return true;
      }
      return false;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      const firstLineStart = v.lastIndexOf("\n", selStart - 1) + 1;
      const lines = v.slice(firstLineStart, selEnd).split("\n");
      if (event.shiftKey) {
        let removedFirst = 0;
        let removedTotal = 0;
        const out = lines
          .map((ln, i) => {
            const rm = ln.match(/^ {1,2}/);
            const n = rm ? rm[0].length : 0;
            if (i === 0) removedFirst = n;
            removedTotal += n;
            return ln.slice(n);
          })
          .join("\n");
        editValue(
          v.slice(0, firstLineStart) + out + v.slice(selEnd),
          Math.max(firstLineStart, selStart - removedFirst),
          selEnd - removedTotal,
        );
      } else {
        const out = lines.map((ln) => "  " + ln).join("\n");
        editValue(
          v.slice(0, firstLineStart) + out + v.slice(selEnd),
          selStart + 2,
          selEnd + 2 * lines.length,
        );
      }
      return true;
    }
    return false;
  }

  return (
    <article
      className={`soap-card ${suggestion ? "has-suggestion" : ""} ${isFocused ? "is-focused" : ""} ${aiAuthored ? "ai-authored" : ""} ${generating ? "ai-generating" : ""}`}
      data-testid={`soap-card-${id}`}
    >
      <div className="soap-card-head">
        <div>
          <span className="soap-letter">{letter}</span>
          <div>
            <h3>{label}</h3>
            <p>{hint}</p>
          </div>
        </div>
        {aiAuthored ? (
          <span className="ai-label autopilot"><Sparkles size={12} /> {t("soap.ai.autopilot")}</span>
        ) : suggestion ? (
          <span className="ai-label"><Sparkles size={12} /> Heartbeat</span>
        ) : null}
      </div>

      <div className="soap-field-shell">
        <textarea
          ref={textareaRef}
          aria-label={`${label} clinical note`}
          value={value}
          placeholder={t("soap.placeholder")}
          onFocus={() => onFocus(id)}
          onInput={(event) => {
            const target = event.currentTarget;
            target.style.height = "auto";
            target.style.height = `${Math.max(82, target.scrollHeight)}px`;
          }}
          onChange={(event) => {
            onChange(id, event.target.value);
            if (suggestion) onDismissSuggestion(id);
          }}
          onKeyDown={(event) => {
            if (event.key === "Tab" && suggestion) {
              event.preventDefault();
              onAcceptSuggestion(suggestion);
              return;
            }
            if (event.key === "Escape" && suggestion) {
              event.preventDefault();
              onDismissSuggestion(id);
              return;
            }
            handleListKeys(event);
          }}
        />
        {suggestion && (
          <div className="ghost-suggestion" tabIndex={0}>
            <span className="ghost-copy">{suggestion.text}</span>
            <span className="ghost-actions">
              <button
                type="button"
                onClick={() => onAcceptSuggestion(suggestion)}
                aria-label={`Accept ${label} suggestion`}
                title={t("soap.accept.tab")}
              >
                <Check size={13} />
              </button>
              <button
                type="button"
                onClick={() => onDismissSuggestion(id)}
                aria-label={`Dismiss ${label} suggestion`}
                title={t("soap.dismiss.esc")}
              >
                <X size={13} />
              </button>
            </span>
            <span className="evidence-tooltip" role="tooltip">
              <strong>{t("soap.evidence")}</strong> {suggestion.evidence}
              <em>{suggestion.why}</em>
            </span>
          </div>
        )}
        {generating && (
          <span className="ai-generating-overlay" aria-hidden="true">
            <Sparkles size={12} /> {t("soap.generating")}
          </span>
        )}
        {children}
      </div>
    </article>
  );
}

